import { useState } from 'react'

import { pieceCardHref } from '@/components/piece/PieceReference'
import { SectionCard } from '@/components/warehouse/primitives'
import { WarehouseThumbnail } from '@/components/warehouse/WarehouseThumbnail'
import { applyAttributionActions, previewAttributionActions } from '@/lib/api/client/product-attribution'
import type { AttributionAction, AttributionActionResult, PieceEvidence, ProductEvidence } from '@/lib/api/schemas/product-attribution.schema'
import { warehouseProductStatusLabels } from '@/lib/warehouse/labels'

const ACTION_LABELS: Record<AttributionAction['kind'], string> = {
  reassign_listing: 'Перенести связь с витриной',
  reassign_sales_link: 'Перенести связь продажи',
  reassign_order_relation: 'Перенести связь заказа',
  move_media: 'Перенести фото',
}

type EvidenceActionRow = {
  id?: string
  sourceId?: string
  externalOrderId?: string
  position?: number
  target: string
  primary?: boolean
}

type AttributionPreview = Awaited<ReturnType<typeof previewAttributionActions>>

function actionFor(kind: 'listing' | 'sales' | 'relation' | 'media', row: EvidenceActionRow): AttributionAction {
  if (kind === 'listing') return { kind: 'reassign_listing', listingId: row.id ?? '', targetProductId: row.target }
  if (kind === 'sales') return { kind: 'reassign_sales_link', sourceId: row.sourceId ?? '', externalOrderId: row.externalOrderId ?? '', position: row.position ?? 1, targetProductId: row.target }
  if (kind === 'relation') return { kind: 'reassign_order_relation', sourceId: row.sourceId ?? '', externalOrderId: row.externalOrderId ?? '', position: row.position ?? 1, targetProductId: row.target }
  return { kind: 'move_media', mediaId: row.id ?? '', targetProductId: row.target, primary: row.primary ? 'make_primary' : 'keep_target' }
}

function conflictsForPiece(piece: PieceEvidence): AttributionAction[] {
  return [
    ...piece.listings
      .filter((row) => row.verdict === 'points_at_other_piece' && row.pointsAtProductId !== null)
      .map((row) => actionFor('listing', { id: row.id, target: row.pointsAtProductId ?? '' })),
    ...piece.salesLinks
      .filter((row) => row.verdict === 'points_at_other_piece' && row.pointsAtProductId !== null)
      .map((row) => actionFor('sales', { sourceId: row.sourceId, externalOrderId: row.externalOrderId, position: row.position, target: row.pointsAtProductId ?? '' })),
    ...piece.orderRelations
      .filter((row) => row.verdict === 'points_at_other_piece' && row.pointsAtProductId !== null)
      .map((row) => actionFor('relation', { sourceId: row.sourceId, externalOrderId: row.externalOrderId, position: row.position, target: row.pointsAtProductId ?? '' })),
    ...piece.media
      .filter((row) => row.verdict === 'points_at_other_piece' && row.pointsAtProductId !== null)
      .map((row) => actionFor('media', { id: row.id, target: row.pointsAtProductId ?? '', primary: row.isPrimary })),
  ]
}

function sizeOrder(size: string | null): { numeric: number | null; text: string } {
  const text = size?.trim() ?? ''
  if (text === '') return { numeric: null, text }
  const numeric = Number(text.replace(',', '.'))
  return Number.isFinite(numeric) ? { numeric, text } : { numeric: null, text }
}

function sortedPieces(evidence: ProductEvidence): PieceEvidence[] {
  return [...evidence.siblings].sort((left, right) => {
    const leftCurrent = left.productId === evidence.productId
    const rightCurrent = right.productId === evidence.productId
    if (leftCurrent !== rightCurrent) return leftCurrent ? -1 : 1
    const leftSize = sizeOrder(left.size)
    const rightSize = sizeOrder(right.size)
    if (leftSize.numeric !== null && rightSize.numeric !== null && leftSize.numeric !== rightSize.numeric) {
      return leftSize.numeric - rightSize.numeric
    }
    if (leftSize.numeric !== null && rightSize.numeric === null) return -1
    if (leftSize.numeric === null && rightSize.numeric !== null) return 1
    const byText = leftSize.text.localeCompare(rightSize.text, 'ru')
    return byText !== 0 ? byText : left.productId.localeCompare(right.productId)
  })
}

function primaryMediaUrl(piece: PieceEvidence): string | null {
  const primary = piece.media.find((row) => row.isPrimary && row.status === 'downloaded')
  return primary?.mediaUrl ?? null
}

function russianMessage(message: string): string {
  const translated = message
    .replace(/seller SKU/gi, 'код продавца')
    .replace(/offer ID/gi, 'идентификатор предложения')
  return /[A-Za-z]/.test(translated) ? 'Операция недоступна.' : translated
}

export function SameArticleTable({
  evidence,
  busy,
  onPreview,
}: {
  evidence: ProductEvidence
  busy: boolean
  onPreview: (actions: readonly AttributionAction[]) => Promise<void>
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] border-collapse text-xs">
        <thead>
          <tr className="border-b border-rule text-left text-[10px] uppercase text-ink-muted">
            <th scope="col" className="px-2 py-2 font-medium">УИН</th>
            <th scope="col" className="px-2 py-2 font-medium">Размер</th>
            <th scope="col" className="px-2 py-2 font-medium">Вес, г</th>
            <th scope="col" className="px-2 py-2 font-medium">Состояние</th>
            <th scope="col" className="px-2 py-2 font-medium">Витрины</th>
            <th scope="col" className="px-2 py-2 font-medium">Заказы</th>
            <th scope="col" className="px-2 py-2 font-medium">Фото</th>
            <th scope="col" className="px-2 py-2 font-medium">Открыть</th>
          </tr>
        </thead>
        <tbody>
          {sortedPieces(evidence).map((piece) => {
            const current = piece.productId === evidence.productId
            const actions = evidence.hasConflicts ? conflictsForPiece(piece) : []
            return (
              <tr
                key={piece.productId}
                data-testid="same-article-row"
                data-product-id={piece.productId}
                data-current={current ? 'true' : 'false'}
                className="border-b border-rule/60 align-middle last:border-b-0"
              >
                <td className="px-2 py-2">
                  <div className="flex min-w-[150px] flex-col gap-1">
                    {current ? <span className="w-fit rounded border border-gold/40 bg-gold/5 px-1.5 py-0.5 text-[10px] text-gold-deep">это изделие</span> : null}
                    <span className="font-medium text-ink">{piece.uin ?? '—'}</span>
                    {actions.length > 0 ? <button type="button" disabled={busy} onClick={() => void onPreview(actions)} className="w-fit rounded border border-st-unpaid/30 bg-st-unpaid/5 px-1.5 py-0.5 text-[10px] text-st-unpaid hover:bg-st-unpaid/10">Есть расхождение</button> : null}
                  </div>
                </td>
                <td className="px-2 py-2 text-ink-2">{piece.size ?? '—'}</td>
                <td className="px-2 py-2 text-ink-2">{piece.weightGrams ?? '—'}</td>
                <td className="px-2 py-2 text-ink-2">{warehouseProductStatusLabels[piece.publishedStatus]}</td>
                <td className="px-2 py-2 text-ink-2">{piece.listings.filter((row) => row.supersededAt === null).length}</td>
                <td className="px-2 py-2 text-ink-2">{piece.orderRelations.length}</td>
                <td className="px-2 py-2"><WarehouseThumbnail mediaUrl={primaryMediaUrl(piece)} label={piece.uin ?? 'Изделие'} /></td>
                <td className="px-2 py-2">{current ? <span className="text-ink-muted">—</span> : <a href={pieceCardHref(piece.productId)} className="text-ink underline decoration-rule underline-offset-2 hover:text-gold-deep">Открыть</a>}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function actionEffect(action: AttributionAction, pieces: readonly PieceEvidence[]): string {
  const target = pieces.find((piece) => piece.productId === action.targetProductId)
  const destination = target === undefined ? 'другое изделие' : `изделие ${target.uin ?? '—'}, размер ${target.size ?? '—'}`
  const subject = action.kind === 'reassign_sales_link' || action.kind === 'reassign_order_relation'
    ? `заказ № ${action.externalOrderId}`
    : action.kind === 'reassign_listing' ? 'связь с витриной' : 'фото'
  return `${subject} → ${destination}`
}

function ActionRow({ result, pieces }: { result: AttributionActionResult; pieces: readonly PieceEvidence[] }) {
  return (
    <li className="rounded-md border border-rule bg-paper p-3 text-xs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-ink">{ACTION_LABELS[result.action.kind]}</span>
        {result.refusal ? <span role="alert" className="text-st-unpaid">{russianMessage(result.refusal.message)}</span> : <span className="text-ink-muted">Доказательство подтверждено</span>}
      </div>
      <p className="mt-1 text-ink-2">{actionEffect(result.action, pieces)}</p>
    </li>
  )
}

export function ProductAttributionSection({ evidence, onReload }: { evidence: ProductEvidence; onReload: () => void }) {
  const [preview, setPreview] = useState<{ actions: readonly AttributionAction[]; result: AttributionPreview } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function previewActions(actions: readonly AttributionAction[]): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      setPreview({ actions: [...actions], result: await previewAttributionActions(actions) })
    } catch (reason: unknown) {
      setError(russianMessage(reason instanceof Error ? reason.message : 'Предпросмотр не выполнен.'))
    } finally {
      setBusy(false)
    }
  }

  async function applyPreview(): Promise<void> {
    if (preview === null) return
    setBusy(true)
    setError(null)
    try {
      await applyAttributionActions(preview.actions, preview.result.previewDigest)
      setPreview(null)
      onReload()
    } catch (reason: unknown) {
      setError(russianMessage(reason instanceof Error ? reason.message : 'Исправление не применено.'))
    } finally {
      setBusy(false)
    }
  }

  const canApply = preview !== null && preview.result.items.length > 0 && preview.result.items.every((result) => result.refusal === null)

  return (
    <SectionCard title="С таким же артикулом" ariaLabel="С таким же артикулом">
      <SameArticleTable evidence={evidence} busy={busy} onPreview={previewActions} />
      {error ? <p role="alert" className="mt-3 text-xs text-st-unpaid">{error}</p> : null}
      {preview ? <div className="mt-4"><p className="text-xs font-medium text-ink-2">Предпросмотр изменений</p><ul className="mt-2 space-y-2">{preview.result.items.map((result, index) => <ActionRow key={`${result.previewDigest}-${index}`} result={result} pieces={evidence.siblings} />)}</ul>{canApply ? <button type="button" disabled={busy} onClick={() => void applyPreview()} className="mt-3 rounded-md border border-gold/40 px-2.5 py-1.5 text-xs text-gold-deep hover:bg-gold/10">Применить</button> : null}</div> : null}
    </SectionCard>
  )
}
